import { phone } from "phone";

const public_api_key = 'WE2AtN';
const klaviyo_list_id = 'SCjt8C';

// Klaviyo API revision (ISO 8601 release date) sent with every client-side request.
// https://developers.klaviyo.com/en/docs/api_versioning_and_deprecation_policy
const klaviyo_api_revision = '2026-07-15';
const klaviyo_content_type = 'application/vnd.api+json';

// Klaviyo dedupes on unique_id anyway; this just avoids firing the same pair of
// requests twice if the customer clicks Submit again after MPR rejects the form.
const sent_registrations = new Set();

// Read the form by field name rather than by position. The serial number, and the
// visible state and country inputs, have no name attribute and so never appear in
// FormData - the serial is read from the DOM below, and state and country are
// carried by their hidden inputs.
function readFields(form) {
  const data = new FormData(form);

  const text = (name) => {
    const value = data.get(name);
    return typeof value === 'string' ? value : '';
  };

  const file = (name) => {
    const value = data.get(name);
    return value instanceof File && value.size > 0 ? value : null;
  };

  // Both consent checkboxes share a generated name, so match on their values. An
  // unchecked box is simply absent from FormData.
  const values = [...data.values()].filter((value) => typeof value === 'string');

  return {
    consentsToSmsMarketing: values.includes('consent_sms_marketing'),
    consentsToEmailMarketing: values.includes('consent_email_marketing'),
    purchasePlace: text('PurchasePlace'),
    productName: text('ProductName'),
    purchaseDate: text('purchaseDate'),
    firstName: text('firstName'),
    lastName: text('surname'),
    address1: text('address1'),
    address2: text('address2'),
    city: text('city'),
    state: text('state'),
    postCode: text('postCode'),
    country: text('country'),
    email: text('email'),
    phone: text('phone'),
    receipt: file('Receipt')
  };
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function sendKlaviyoEvent(registration) {
  const fields = registration.fields;

  // keepalive lets the request outlive the redirect MPR triggers on success.
  return fetch(`https://a.klaviyo.com/client/events?company_id=${public_api_key}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      accept: klaviyo_content_type,
      revision: klaviyo_api_revision,
      'content-type': klaviyo_content_type
    },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          properties: {
            SerialNumber: registration.serialNumber,
            PurchasePlace: fields.purchasePlace,
            ProductName: fields.productName,
            PurchaseDate: fields.purchaseDate,
            First_Name: fields.firstName,
            Last_Name: fields.lastName,
            Address_One: fields.address1,
            Address_Two: fields.address2,
            City: fields.city,
            State: fields.state,
            Postcode: fields.postCode,
            Country: fields.country,
            Email: fields.email,
            Phone: fields.phone,
            Receipt: registration.receipt
          },
          time: new Date().toISOString(),
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: 'Product Registration Form Submitted'
              }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: registration.profileAttributes
            }
          },
          unique_id: registration.uniqueId
        }
      }
    })
  })
    .then(response => response.text())
    .then(response => console.log(response))
    .catch(err => console.error(err));
}

function sendKlaviyoSubscription(registration) {
  const subscriptions = {};

  if (registration.fields.consentsToEmailMarketing) {
    subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
  }

  if (registration.fields.consentsToSmsMarketing) {
    subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
  }

  const attributes = {
    email: registration.fields.email,
    subscriptions
  };

  if (registration.phoneNumber) {
    attributes.phone_number = registration.phoneNumber;
  }

  return fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${public_api_key}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      accept: klaviyo_content_type,
      revision: klaviyo_api_revision,
      'content-type': klaviyo_content_type
    },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Product registration',
          profile: {
            data: {
              type: 'profile',
              attributes
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: klaviyo_list_id } }
        }
      }
    })
  })
    .then(response => response.text())
    .then(response => console.log(response))
    .catch(err => console.error(err));
}

function submitKlaviyoRegistration() {
  const form = document.querySelector('#mpr-product-inputs form');
  if (!form) return;

  const fields = readFields(form);

  // The serial number is not in FormData - its input has no name attribute, and MPR
  // blanks that input once the value is accepted and rendered here instead.
  const serialNumberElement = document.querySelector('.mpr-serial-number-value');
  const serialNumber = serialNumberElement ? serialNumberElement.innerText.trim() : '';
  const email = fields.email.trim().toLowerCase();

  // Our own minimum bar: an accepted serial number, and an email address Klaviyo will
  // accept as a profile identifier. Every other field is optional here - MPR enforces
  // its own required fields separately.
  if (!serialNumber || !isValidEmail(email)) return;

  // A readable, normalised dedupe key. Normalising means a resubmit with differently
  // cased whitespace still dedupes, and the delimiter stops the serial and email
  // running together into a value another pair could also produce.
  const uniqueId = `product-registration:${serialNumber}:${email}`;
  if (sent_registrations.has(uniqueId)) return;
  sent_registrations.add(uniqueId);

  // If an image was uploaded, send the rendered preview's source.
  const receiptImage = fields.receipt ? document.querySelector('.mpr-uploaded-files-item img') : null;
  const receipt = receiptImage ? receiptImage.getAttribute('src') || '' : '';

  // Only send phone number if consented, otherwise Klaviyo auto-subscribes to SMS marketing
  const phoneNumber = fields.phone && fields.consentsToSmsMarketing
    ? phone(fields.phone).phoneNumber
    : null;

  const profileAttributes = {
    email: fields.email,
    first_name: fields.firstName,
    last_name: fields.lastName,
    location: {
      address1: fields.address1,
      address2: fields.address2,
      city: fields.city,
      region: fields.state,
      zip: fields.postCode,
      country: fields.country
    },
    properties: {
      product_0_serialNumbers: serialNumber,
      product_0_PurchasePlace: fields.purchasePlace,
      product_0_ProductName: fields.productName,
      product_0_purchaseDate: fields.purchaseDate,
      product_0_Receipt: receipt
    }
  };

  if (phoneNumber) {
    profileAttributes.phone_number = phoneNumber;
  }

  const registration = { fields, serialNumber, receipt, uniqueId, phoneNumber, profileAttributes };

  // Fired together rather than chained: MPR redirects to "/" on success, so both
  // requests need to be in flight before the page starts unloading.
  sendKlaviyoEvent(registration);

  if (fields.consentsToEmailMarketing || fields.consentsToSmsMarketing) {
    sendKlaviyoSubscription(registration);
  }
}

function setUpKlaviyoProductForm() {
  document
    .querySelector('#mpr-product-inputs .form-group button')
    .addEventListener('click', submitKlaviyoRegistration);
}

const loadPRFormInterval = setInterval(function () {
  if (document.querySelector('#mpr-product-inputs .form-group button')) {
    setUpKlaviyoProductForm();
    console.log('Klaviyo Connected To Product Registration Form');
    clearInterval(loadPRFormInterval);
  }
}, 1000);
